<script>
    import { evidenceStore as evidenceStoreObj, saveEvidence, removeSavedEvidence, searchEvidence, getEvidenceBySource, getNonOWIDEvidence } from "../stores/evidenceStore";
    import { promptStore } from "../stores/promptStore";
    import { onMount, onDestroy, createEventDispatcher, getContext } from 'svelte';
    import { storyStore } from '../stores/storyStore.js';
    import EvidenceModal from './EvidenceModal.svelte';
    import { authStore } from '../stores/authStore.js';
    import { API_BASE_URL } from '../config/apiConfig.js';
    import { fade } from 'svelte/transition';

    export let externalHandleEvidenceClick = undefined;

    let debounceTimeout = null;
    let lastSearchQuery = "";
    let searchQuery = "";
    let currentPage = 1;
    const itemsPerPage = 10;
    let selectedEvidenceIds = new Set();
    let isLoading = false;
    let deleteConfirmOpen = false;
    let evidenceToDelete = null;
    const dispatch = createEventDispatcher();
    let evidence = [];
    let savedEvidence = [];
    let showModal = false;
    let selectedEvidence = null;
    let updateModalOpen = false;
    let evidenceToUpdate = null;
    let updateForm = { label: '', description: '' };
    let selectedTab = 'all';
    let showFullDescription = {};
    let activeTab = selectedTab;
    let loadingProgress = { total: 0, loaded: 0, isLoading: false };
    let showMenu = null;
    let showSearchTips = false;
    let searchTextarea;
    let hasPerformedInitialSearch = false;
    let isSearching = false;
    let autoSearchAttempted = false;
    let showLongQueryWarning = false;
    let owidEvidence = [];
    let otherEvidence = [];
    let activeFilter = 'all';
    let filteredEvidence = evidence;

    $: isAdmin = $authStore?.user?.isAdmin || false;

    $: paginatedFilteredEvidence = filteredEvidence.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    $: totalPages = Math.ceil(filteredEvidence.length / itemsPerPage);
    $: selectedCount = selectedEvidenceIds.size;
    $: visiblePages = getVisiblePages(currentPage, totalPages);
    $: if (activeFilter === 'all') {
      filteredEvidence = evidence;
    } else if (activeFilter === 'owid') {
      filteredEvidence = getEvidenceBySource('OWID');
    } else if (activeFilter === 'nonowid') {
      filteredEvidence = getNonOWIDEvidence();
    }

    function autoResize() {
        if (!searchTextarea) return;
        searchTextarea.style.height = 'auto';
        searchTextarea.style.height = searchTextarea.scrollHeight + 'px';
    }
    function handleResize() { autoResize(); }

    const unsubscribeEvidence = evidenceStoreObj.subscribe(store => {
        evidence = store.evidence || [];
        savedEvidence = store.savedEvidence || [];
        loadingProgress = store.loadingProgress;
        // If a bigQuestion is present, filter OWID evidence to match the query and limit
        if ($storyStore.bigQuestion && evidence.length > 0) {
            owidEvidence = evidence.filter(e => e.source === 'OWID');
            // Optionally, limit OWID evidence to 50
            owidEvidence = owidEvidence.slice(0, 50);
        }
    });
    const unsubscribePrompt = promptStore.subscribe(store => {
        if (store.bigQuestion && !hasPerformedInitialSearch && !isSearching) {
            searchEvidenceWithFallbacks(store.bigQuestion, true);
        }
    });

    async function searchEvidenceWithFallbacks(searchTerm = "", isInitialSearch = false) {
        if (isSearching) return false;
        if (evidence.length > 0 && isInitialSearch && !searchTerm) {
            hasPerformedInitialSearch = true;
            return true;
        }
        try {
            const query = (searchTerm || searchQuery || $storyStore.bigQuestion || "").trim();
            const bigQuestion = $storyStore.bigQuestion || "";
            isSearching = true;
            autoSearchAttempted = true;
            currentPage = 1;
            try {
                await searchEvidence(query, { bigQuestion });
                hasPerformedInitialSearch = true;
                return true;
            } catch (storeError) {}
            try {
                const searchUrl = `${API_BASE_URL}/api/evidence/search?query=${encodeURIComponent(query)}&bigQuestion=${encodeURIComponent(bigQuestion)}`;
                const response = await fetch(searchUrl);
                if (response.ok) {/* fallback logic omitted */}
            } catch (directError) {}
            const response = await fetch(`${API_BASE_URL}/api/evidence`);
            if (response.ok) {
                const data = await response.json();
                evidenceStoreObj.update(store => ({}));
                hasPerformedInitialSearch = true;
                return true;
            }
            throw new Error("All evidence loading attempts failed");
        } catch (error) {
            return false;
        } finally {
            isSearching = false;
        }
    }

    function handleEvidenceClick(evidence, event) {
        if (event) event.stopPropagation();
        if (typeof externalHandleEvidenceClick === 'function') {
            externalHandleEvidenceClick(evidence, event);
            return;
        }
        selectedEvidence = evidence;
        showModal = true;
    }
    function handleAnalyzeClick(evidence, event) {
        event.stopPropagation();
        selectedEvidence = evidence;
        showModal = true;
    }
    function closeModal() {
        showModal = false;
        selectedEvidence = null;
    }
    function nextPage() {
      if (currentPage * itemsPerPage < evidence.length) currentPage += 1;
    }
    function prevPage() {
      if (currentPage > 1) currentPage -= 1;
    }
    function getVisiblePages(current, total) {
        if (total <= 7) return Array.from({length: total}, (_, i) => i + 1);
        let pages = [1];
        if (current <= 4) pages.push(2, 3, 4, 5);
        else if (current >= total - 3) pages.push('...', total - 4, total - 3, total - 2, total - 1);
        else pages.push('...', current - 2, current - 1, current, current + 1, current + 2);
        if (pages[pages.length - 1] !== total) {
            if (pages[pages.length - 1] !== total - 1) pages.push('...');
            pages.push(total);
        }
        return pages;
    }
    function handleAddEvidence(evidence, event) {
        event.stopPropagation();
        dispatch('addImageToCanvas', { evidence, type: 'evidence' });
    }
    function handleSaveEvidence(evidence, event) {
        event.stopPropagation();
        if (!evidence) return;
        const isAlreadySaved = savedEvidence.some(e => e._id === evidence._id);
        if (isAlreadySaved) removeSavedEvidence(evidence._id);
        else saveEvidence(evidence);
    }
    function onEvidenceClick(evidence) { handleEvidenceClick(evidence); }
    function unsaveEvidence(evidence) {
        showMenu = null;
        removeSavedEvidence(evidence._id);
    }
    function handleClickOutside(event) {
        if (showMenu !== null && !event.target.closest('.evidence-menu')) showMenu = null;
    }
    $: if (searchQuery) setTimeout(autoResize, 0);
    $: if (activeTab === 'all' && evidence.length === 0 && !isSearching && !autoSearchAttempted) {
        autoSearchAttempted = true;
        // Only use bigQuestion for initial search, never empty string
        if ($storyStore.bigQuestion) {
            searchEvidenceWithFallbacks($storyStore.bigQuestion, true);
        }
    }
    $: if (!autoSearchAttempted && !isSearching && evidence.length === 0 && $authStore.isLoggedIn) {
        autoSearchAttempted = true;
        if ($storyStore.bigQuestion) {
            searchEvidenceWithFallbacks($storyStore.bigQuestion, true);
        }
    }
    async function deleteEvidence(evidence) {
        try {
            const response = await fetch('/api/admin/evidence/main', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: evidence._id })
            });
            if (!response.ok) throw new Error(await response.text());
            let result;
            try { result = await response.json(); } catch (e) { throw new Error('Invalid response from server'); }
            if (result.success) {
                evidenceStoreObj.update(store => ({}));
                closeDeleteConfirm();
            } else throw new Error(result.message || 'Failed to delete evidence');
        } catch (error) {
            alert('Failed to delete evidence. Please try again.');
        }
    }
    function openDeleteConfirm(evidence) {
        evidenceToDelete = evidence;
        deleteConfirmOpen = true;
    }
    function closeDeleteConfirm() {
        deleteConfirmOpen = false;
        evidenceToDelete = null;
    }
    async function updateEvidence() {
        try {
            const response = await fetch('/api/admin/evidence', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: evidenceToUpdate._id,
                    label: updateForm.label,
                    description: updateForm.description
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Failed to update evidence');
            evidence = evidence.map(item => item._id === evidenceToUpdate._id ? { ...item, label: updateForm.label, description: updateForm.description } : item);
            closeUpdateModal();
            alert('Evidence updated successfully');
        } catch (error) {
            alert(error.message || 'Failed to update evidence');
        }
    }
    function openUpdateModal(evidence) {
        evidenceToUpdate = evidence;
        updateForm = { label: evidence.label || '', description: evidence.description || '' };
        updateModalOpen = true;
    }
    function closeUpdateModal() {
        updateModalOpen = false;
        evidenceToUpdate = null;
        updateForm = { label: '', description: '' };
    }
    function isVeryLongQuery(query) {
        return query.split(/\s+/).filter(term => term.length > 1).length > 8;
    }
    function handleSearchInput(event) {
        const value = event.target.value;
        searchQuery = value;
        if (debounceTimeout) clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => {
            if (value.trim() && value.trim() !== lastSearchQuery) {
                lastSearchQuery = value.trim();
                searchEvidenceWithFallbacks(value.trim());
            }
        }, 400);
    }
    function handleSearchKeydown(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (debounceTimeout) clearTimeout(debounceTimeout);
            const value = event.target.value.trim();
            if (value && value !== lastSearchQuery) {
                lastSearchQuery = value;
                searchEvidenceWithFallbacks(value);
            }
        }
    }
    async function handleSearch(event) {
        event.preventDefault();
        if (debounceTimeout) clearTimeout(debounceTimeout);
        const query = searchQuery.trim() || $storyStore.bigQuestion;
        if (!query) return;
        if (query !== lastSearchQuery) {
            lastSearchQuery = query;
            showLongQueryWarning = isVeryLongQuery(query);
            await searchEvidenceWithFallbacks(query);
        }
    }

    onDestroy(() => {
        unsubscribeEvidence();
        unsubscribePrompt();
        window.removeEventListener('resize', handleResize);
    });
    onMount(async () => {
        window.addEventListener('resize', handleResize);
        if ($storyStore.bigQuestion && !searchQuery) {
            searchQuery = $storyStore.bigQuestion;
            setTimeout(autoResize, 0);
        }
        // Only use bigQuestion for initial search, never empty string
        if ($storyStore.bigQuestion) {
            await searchEvidenceWithFallbacks($storyStore.bigQuestion, true);
        }
        const unsubscribeEvidenceTypes = evidenceStoreObj.subscribe(store => {
            owidEvidence = getEvidenceBySource('OWID');
            otherEvidence = getNonOWIDEvidence();
        });
        const unsubscribeStory = storyStore.subscribe(store => {
            if (store.bigQuestion && store.bigQuestion !== searchQuery) {
                searchQuery = store.bigQuestion;
                setTimeout(autoResize, 0);
            }
            // Always use bigQuestion for search if available
            if (store.bigQuestion && !hasPerformedInitialSearch && !isSearching) {
                searchEvidenceWithFallbacks(store.bigQuestion, true);
            }
        });
        let visibilityObserver;
        try {
            const targetElement = document.querySelector('.evidence-container');
            if (targetElement) {/* observer logic omitted */}
        } catch (error) {}
        if ($authStore.isLoggedIn) {
            const initialSearchTerm = searchQuery || $storyStore.bigQuestion || "";
            await searchEvidenceWithFallbacks(initialSearchTerm, true);
            if (evidence.length === 0 && !isSearching) {/* fallback omitted */}
        }
        return () => {
            window.removeEventListener('resize', handleResize);
            if (visibilityObserver) {/* observer cleanup omitted */}
            unsubscribeStory();
            unsubscribeEvidenceTypes();
        };
    });

    // Helper to get the correct image URL for evidence (PEW or OWID)
    function getEvidenceImageUrl(evidence) {
        return evidence.s3URL || evidence.mediaURL || evidence.svgURL || '';
    }

    // Add function to check if query is very long
    // (Removed duplicate declaration of isVeryLongQuery)

</script>

<div class="evidence-container">
    <div class="top-container">
        <div class="tabs-container">
            <div class="left-tabs">
                <button 
                    class="tab-button" 
                    class:active={activeTab === 'all'}
                    on:click={() => {
                        activeTab = 'all';
                        // If we have no evidence, trigger a search
                        if (evidence.length === 0 && !isSearching) {
                            searchEvidenceWithFallbacks(null, true);
                        }
                    }}
                >
                    <i class="bi bi-search"></i>
                    Search
                </button>
                <button 
                    class="tab-button" 
                    class:active={activeTab === 'saved'}
                    on:click={() => activeTab = 'saved'}
                >
                    <i class="bi bi-bookmark"></i>
                    Saved
                </button>
            </div>
            <button 
                class="tab-button guide-tab" 
                class:active={activeTab === 'guide'}
                on:click={() => activeTab = 'guide'}
            >
                <i class="bi bi-lightbulb"></i>
                Guide Me
            </button>
        </div>

        {#if activeTab === 'guide'}
            <div class="guide-section">
                <div class="guide-content">
                    <div class="guide-iframe-container">
                        <iframe 
                            src="https://docs.google.com/document/d/e/2PACX-1vSZF6f11LfrMZ8b4yD-4q_o-yd-ItdP_G9NfbkkQn7jHpegmmZG7j5vWa4N73nJ-lyFOkSZAGaKGky8/pub?embedded=true"
                            class="guide-iframe"
                            frameborder="0"
                            allowfullscreen
                            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                            on:load={() => {
                                // Hide loading indicator when iframe loads
                                const loadingEl = document.querySelector('.guide-loading');
                                if (loadingEl) loadingEl.style.display = 'none';
                            }}
                            on:error={() => {
                                // Show error message if iframe fails to load
                                const loadingEl = document.querySelector('.guide-loading');
                                if (loadingEl) {
                                    loadingEl.innerHTML = `
                                        <i class="bi bi-exclamation-triangle"></i>
                                        <span>Failed to load guide. Please try refreshing the page.</span>
                                    `;
                                }
                            }}
                        ></iframe>
                        <div class="guide-loading">
                            <i class="bi bi-arrow-repeat spinner"></i>
                            <span>Loading guide...</span>
                        </div>
                    </div>
                </div>
            </div>
        {:else if activeTab === 'saved'}
            <div class="scrollable-content">
                {#if loadingProgress.isLoading || isSearching}
                    <div class="loading-container">
                        <div class="loading-content">
                            {#if loadingProgress.total > 0}
                                <div class="progress-bar">
                                    <div 
                                        class="progress-fill" 
                                        style="width: {(loadingProgress.loaded / loadingProgress.total) * 100}%"
                                    ></div>
                                </div>
                                <div class="progress-text">
                                    Loading data charts: {loadingProgress.loaded} of {loadingProgress.total}
                                </div>
                            {:else}
                                <div class="loading-spinner">
                                    <i class="bi bi-arrow-repeat spinner"></i>
                                </div>
                            {/if}
                        </div>
                    </div>
                {:else if isLoading}
                    <div class="loading-container">
                        <div class="loading-spinner">
                            <i class="bi bi-arrow-repeat spinner"></i>
                        </div>
                    </div>
                {:else}
                    {#if savedEvidence.length === 0}
                        <div class="empty-state">
                            <p>
                                You have no data charts saved. Use this icon <i class="fas fa-bookmark"></i> to save a data chart.
                            </p>
                        </div>
                    {:else}
                        <div class="evidence-list">
                            {#each savedEvidence as evidence (evidence._id)}
                                <div class="evidence-item" on:click={(e) => handleAnalyzeClick(evidence, e)}>
                                    {#if isAdmin}
                                        <div class="admin-menu">
                                            <button 
                                                class="admin-menu-button"
                                                on:click|stopPropagation={() => showMenu = evidence._id}
                                                title="Admin Options"
                                            >
                                                <i class="bi bi-gear"></i>
                                            </button>
                                            {#if showMenu === evidence._id}
                                                <div class="admin-menu-dropdown">
                                                    <button 
                                                        class="menu-item"
                                                        on:click|stopPropagation={() => openUpdateModal(evidence)}
                                                    >
                                                        <i class="bi bi-pencil"></i>
                                                        Edit Evidence
                                                    </button>
                                                    <button 
                                                        class="menu-item delete-item"
                                                        on:click|stopPropagation={() => openDeleteConfirm(evidence)}
                                                    >
                                                        <i class="bi bi-trash"></i>
                                                        Delete Evidence
                                                    </button>
                                                </div>
                                            {/if}
                                        </div>
                                    {/if}
                                    <button 
                                        class="bookmark-button saved" 
                                        on:click={(e) => handleSaveEvidence(evidence, e)}
                                        title="Remove Data Chart"
                                    >
                                        <i class="bi bi-bookmark-fill"></i>
                                    </button>
                                    <div class="image-container">
                                        <img 
                                            src={getEvidenceImageUrl(evidence)} 
                                            alt={evidence.label}
                                            class="evidence-image"
                                        />
                                    </div>
                                    <div class="content-wrapper">
                                        <div class="evidence-label">
                                            {evidence.label_easy || evidence.label}
                                        </div>
                                        {#if evidence.description_easy || evidence.description}
                                            <div class="evidence-description-wrapper">
                                                <div 
                                                    class="evidence-description" 
                                                    class:expanded={showFullDescription[evidence._id]}
                                                    aria-expanded={showFullDescription[evidence._id]}
                                                >
                                                    {evidence.description_easy || evidence.description}
                                                </div>
                                                <button 
                                                    class="expand-button"
                                                    on:click|stopPropagation={() => {
                                                        showFullDescription[evidence._id] = !showFullDescription[evidence._id];
                                                    }}
                                                    aria-label={showFullDescription[evidence._id] ? 'Collapse description' : 'Expand description'}
                                                    aria-controls="description-{evidence._id}"
                                                >
                                                    <i class="bi bi-{showFullDescription[evidence._id] ? 'chevron-up' : 'chevron-down'}"></i>
                                                </button>
                                            </div>
                                        {/if}
                                    </div>
                                </div>
                            {/each}
                        </div>
                    {/if}
                {/if}
            </div>

            {#if selectedTab === 'all' && (evidence.length > itemsPerPage || (!isSearching && evidence.length > 0))}
                <!-- Update pagination wrapper to include results counter -->
                <div class="pagination-container">
                    <div class="pagination-wrapper">
                        {#if !isSearching && filteredEvidence.length > 0}
                            <div class="results-counter">
                                {#if filteredEvidence.length > itemsPerPage}
                                    Showing {paginatedFilteredEvidence.length} of {filteredEvidence.length} data chart{filteredEvidence.length === 1 ? '' : 's'}
                                {:else}
                                    Found {filteredEvidence.length} data chart{filteredEvidence.length === 1 ? '' : 's'}
                                {/if}
                            </div>
                        {/if}
                        {#if evidence.length > itemsPerPage}
                            <div class="pagination">
                                <button 
                                    class="page-btn" 
                                    disabled={currentPage === 1}
                                    on:click={prevPage}
                                    title="Previous page"
                                >
                                    <i class="bi bi-chevron-left"></i>
                                </button>

                                {#each visiblePages as page}
                                    {#if page === '...'}
                                        <span class="ellipsis">•••</span>
                                    {:else}
                                        <button 
                                            class="page-btn {currentPage === page ? 'active' : ''}"
                                            on:click={() => currentPage = page}
                                        >
                                            {page}
                                        </button>
                                    {/if}
                                {/each}

                                <button 
                                    class="page-btn"
                                    disabled={currentPage === totalPages}
                                    on:click={nextPage}
                                    title="Next page"
                                >
                                    <i class="bi bi-chevron-right"></i>
                                </button>
                            </div>
                        {/if}
                    </div>
                </div>
            {/if}
        {:else}
            <div class="search-bar">
                <div class="search-container">
                    <div class="input-container">
                        <i class="bi bi-search search-icon"></i>
                        <form on:submit|preventDefault={handleSearch}>
                            <textarea
                                class="search-input"
                                bind:this={searchTextarea}
                                bind:value={searchQuery}
                                placeholder="Search for evidence, e.g. 'social media impact on youth'"
                                rows="1"
                                on:input={handleSearchInput}
                                on:keydown={handleSearchKeydown}
                            />
                            
                            {#if searchQuery}
                                <button 
                                    type="button"
                                    class="clear-button" 
                                    on:click={() => {
                                        searchQuery = '';
                                        setTimeout(autoResize, 0);
                                    }} 
                                    aria-label="Clear search"
                                >
                                    <i class="bi bi-x-lg"></i>
                                </button>
                            {/if}
                        </form>
                    </div>
                </div>
                <div class="filter-dropdown">
  <select
    id="evidence-filter"
    class="filter-select"
    bind:value={activeFilter}
    on:change={() => {
      currentPage = 1;
      if (activeFilter === 'all') {
        filteredEvidence = evidence;
      } else if (activeFilter === 'owid') {
        filteredEvidence = getEvidenceBySource('OWID');
      } else if (activeFilter === 'nonowid') {
        filteredEvidence = getNonOWIDEvidence();
      }
    }}
  >
    <option value="all">All Evidence</option>
    <option value="owid">OWID Only</option>
    <option value="nonowid">Pew Research Center Only</option>
  </select>
</div>
            </div>

            <div class="scrollable-content">
                {#if loadingProgress.isLoading || isSearching}
                    <div class="loading-container">
                        <div class="loading-content">
                            {#if loadingProgress.total > 0}
                                <div class="progress-bar">
                                    <div 
                                        class="progress-fill" 
                                        style="width: {(loadingProgress.loaded / loadingProgress.total) * 100}%"
                                    ></div>
                                </div>
                                <div class="progress-text">
                                    Loading data charts: {loadingProgress.loaded} of {loadingProgress.total}
                                </div>
                            {:else}
                                <div class="loading-spinner">
                                    <i class="bi bi-arrow-repeat spinner"></i>
                                </div>
                            {/if}
                        </div>
                    </div>
                {:else if isLoading}
                    <div class="loading-container">
                        <div class="loading-spinner">
                            <i class="bi bi-arrow-repeat spinner"></i>
                        </div>
                    </div>
                {:else if filteredEvidence.length === 0 && searchQuery}
                    <div class="no-results">
                        <i class="bi bi-emoji-frown"></i>
                        {#if activeFilter === 'owid'}
                            <p>No OWID data charts found matching "{searchQuery}"</p>
                        {:else if activeFilter === 'nonowid'}
                            <p>No Pew Research Center data charts found matching "{searchQuery}"</p>
                        {:else}
                            <p>No data charts found matching "{searchQuery}"</p>
                        {/if}
                        {#if showLongQueryWarning && evidence.length === 0}
                            <div class="search-suggestion warning">
                                <p><i class="bi bi-info-circle"></i> Your search query is quite long. Try a simpler search with just the key terms.</p>
                                <div class="simplified-terms">
                                    {#each searchQuery.split(/\s+/).filter(term => term.length > 3 && !['with', 'that', 'this', 'from', 'about', 'their', 'they', 'have', 'what', 'should', 'would', 'could', 'does', 'where', 'when', 'people'].includes(term.toLowerCase())).slice(0, 5) as term}
                                        <button 
                                            class="term-button"
                                            on:click={() => {
                                                searchQuery = term;
                                                setTimeout(autoResize, 0);
                                                isSearching = true;
                                                searchEvidence(term)
                                                    .catch(error => {
                                                        console.error('Search failed:', error);
                                                    })
                                                    .finally(() => {
                                                        isSearching = false;
                                                    });
                                            }}
                                        >
                                            {term}
                                        </button>
                                    {/each}
                                </div>
                            </div>
                        {/if}
                        <button 
                            class="try-again-button"
                            on:click={() => {
                                searchQuery = '';
                                setTimeout(autoResize, 0);
                            }}
                        >
                            Clear search
                        </button>
                    </div>
                {:else}
                    <div class="evidence-list">
                        {#each paginatedFilteredEvidence as evidence, i (evidence._id)}
                            {#if !(evidence.source === 'OWID' && evidence.type === 'explorer' && !evidence.imageURL && !evidence.s3URL && !evidence.mediaURL && !evidence.mediaS3URL)}
                        
                            <div 
                                class="evidence-item"
                                on:click={(e) => handleAnalyzeClick(evidence, e)}
                                style="animation-delay: {i * 50}ms"
                            >
                                {#if isAdmin}
                                    <div class="admin-menu">
                                <button 
                                            class="admin-menu-button"
                                            on:click|stopPropagation={() => showMenu = evidence._id}
                                            title="Admin Options"
                                        >
                                            <i class="bi bi-gear"></i>
                                        </button>
                                        
                                        {#if showMenu === evidence._id}
                                            <div class="admin-menu-dropdown">
                                                <button 
                                                    class="menu-item"
                                                    on:click|stopPropagation={() => openUpdateModal(evidence)}
                                                >
                                                    <i class="bi bi-pencil"></i>
                                                    Edit Evidence
                                                </button>
                                                <button 
                                                    class="menu-item delete-item"
                                                    on:click|stopPropagation={() => openDeleteConfirm(evidence)}
                                                >
                                                    <i class="bi bi-trash"></i>
                                                    Delete Evidence
                                                </button>
                                            </div>
                                        {/if}
                                    </div>
                                {/if}
                                <button 
                                    class="bookmark-button {savedEvidence.some(e => e._id === evidence._id) ? 'saved' : ''}" 
                                    on:click={(e) => handleSaveEvidence(evidence, e)}
                                    title={savedEvidence.some(e => e._id === evidence._id) ? "Remove Data Chart" : "Save Data Chart"}
                                >
                                    <i class="bi {savedEvidence.some(e => e._id === evidence._id) ? 'bi-bookmark-fill' : 'bi-bookmark'}"></i>
                                </button>
                                <div class="image-container">
                                    <img src={getEvidenceImageUrl(evidence)} alt={evidence.description} class="evidence-image" />
                                </div>
                                <div class="content-wrapper">
                                    <div class="evidence-label">
                                        {evidence.label_easy || evidence.label}
                                    </div>
                                    {#if evidence.description_easy || evidence.description}
                                        <div class="evidence-description-wrapper">
                                            <div 
                                                class="evidence-description" 
                                                class:expanded={showFullDescription[evidence._id]}
                                                aria-expanded={showFullDescription[evidence._id]}
                                            >
                                                {evidence.description_easy || evidence.description}
                                            </div>
                                            <button 
                                                class="expand-button"
                                                on:click|stopPropagation={() => {
                                                    showFullDescription[evidence._id] = !showFullDescription[evidence._id];
                                                }}
                                                aria-label={showFullDescription[evidence._id] ? 'Collapse description' : 'Expand description'}
                                                aria-controls="description-{evidence._id}"
                                            >
                                                <i class="bi bi-{showFullDescription[evidence._id] ? 'chevron-up' : 'chevron-down'}"></i>
                                            </button>
                                        </div>
                                    {/if}
                                </div>
                            </div>
                            {/if}
                        {/each}
                    </div>
                {/if}
            </div>

            {#if selectedTab === 'all' && (evidence.length > itemsPerPage || (!isSearching && evidence.length > 0))}
                <!-- Update pagination wrapper to include results counter -->
                <div class="pagination-container">
                    <div class="pagination-wrapper">
                        {#if !isSearching && evidence.length > 0}
<div class="results-counter">
    {#if filteredEvidence.length > itemsPerPage}
        Showing {paginatedFilteredEvidence.length} of {filteredEvidence.length} data chart{filteredEvidence.length === 1 ? '' : 's'}
    {:else}
        Found {filteredEvidence.length} data chart{filteredEvidence.length === 1 ? '' : 's'}
    {/if}
</div>
                        {/if}
                        {#if evidence.length > itemsPerPage}
                            <div class="pagination">
                                <button 
                                    class="page-btn" 
                                    disabled={currentPage === 1}
                                    on:click={prevPage}
                                    title="Previous page"
                                >
                                    <i class="bi bi-chevron-left"></i>
                                </button>

                                {#each visiblePages as page}
                                    {#if page === '...'}
                                        <span class="ellipsis">•••</span>
                                    {:else}
                                        <button 
                                            class="page-btn {currentPage === page ? 'active' : ''}"
                                            on:click={() => currentPage = page}
                                        >
                                            {page}
                                        </button>
                                    {/if}
                                {/each}

                                <button 
                                    class="page-btn"
                                    disabled={currentPage === totalPages}
                                    on:click={nextPage}
                                    title="Next page"
                                >
                                    <i class="bi bi-chevron-right"></i>
                                </button>
                            </div>
                        {/if}
                    </div>
                </div>
            {/if}
        {/if}
    </div>
</div>

<style>
    .evidence-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        position: relative;
        overflow: hidden;
    }

    .top-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
    }

    .tabs-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: white;
        padding: 0.5rem 1rem;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    }

    .left-tabs {
        display: flex;
        gap: 1rem;
    }

    .tab-button {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        border: none;
        background: none;
        color: #64748b;
        font-size: 0.875rem;
        font-weight: 500;
        border-bottom: 2px solid transparent;
        cursor: pointer;
        transition: all 0.2s ease;
        position: relative;
    }

    .tab-button.active {
        color: #2563eb;
        border-bottom-color: #2563eb;
        background: #eff6ff;
    }

    .tab-button:hover {
        color: #2563eb;
        background: #f8fafc;
    }

    .guide-tab {
        font-size: 0.8125rem;
        padding: 0.5rem 0.75rem;
        background: #f8fafc;
        border-radius: 6px;
        border-bottom: none;
    }

    .guide-tab:hover {
        background: #f1f5f9;
        color: #2563eb;
    }

    .guide-tab.active {
        background: #eff6ff;
        color: #2563eb;
        font-weight: 600;
    }

    .scrollable-content {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        padding-bottom: 160px; /* Increase bottom padding for better visibility */
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain; /* Prevent scroll chaining */
        max-height: none; /* Remove max-height limitation */
    }

    .pagination-container {
        position: sticky; /* Ensures the pagination stays at the bottom while allowing content to scroll */
        bottom: 0;
        left: 0;
        right: 0;
        background: white;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
        padding: 12px;
        z-index: 100;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: auto; /* Push to bottom of container */
    }

    .pagination-wrapper {
        display: flex;
        align-items: center;
        gap: 16px;
        justify-content: center;
        width: 100%;
        max-width: 800px;
    }

    .results-counter {
        color: #64748b;
        font-size: 0.875rem;
        white-space: nowrap;
    }

    .pagination {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px;
        background: #f8fafc;
        border-radius: 8px;
    }

    .page-btn {
        min-width: 32px;
        height: 32px;
        border: none;
        background: transparent;
        color: #64748b;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
        border-radius: 6px;
        padding: 0 8px;
    }

    .page-btn:hover:not(:disabled):not(.active) {
        background-color: #e2e8f0;
        color: #0f172a;
    }

    .page-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .page-btn.active {
        background-color: white;
        color: #2563eb;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .ellipsis {
        color: #94a3b8;
        padding: 0 4px;
        min-width: 32px;
        text-align: center;
        font-size: 14px;
    }

    /* Add styles for the page info */
    .page-info {
        color: #64748b;
        font-size: 13px;
        margin-left: 8px;
    }

    /* Update save button when evidence is saved */
    .save-button.saved {
        color: #007bff;
        background-color: #e6f0ff;
    }

    .search-bar {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    width: 100%;
    gap: 1.5rem;
    /* background-color: #f8fafc; */
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }

    .search-container {
    width: 100%;
    max-width: 800px;
    margin: 0;
    display: flex;
    align-items: center;
    }

    .input-container {
        position: relative;
        width: 100%;
        min-height: 48px; /* Match textarea min-height */
    }

    .search-input {
        width: 100%;
        padding: 0.875rem 7rem;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        font-size: 0.9375rem;
        transition: all 0.2s;
        background: white;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        resize: none;
        overflow: hidden;
        min-height: 48px;
        max-height: 150px; /* Increased max height */
        line-height: 1.5;
        font-family: inherit;
        display: block;
    }

    .search-input:focus {
        outline: none;
        border-color: #2563eb;
    justify-content: flex-end;
    }

    /* Ensure icons stay vertically centered relative to the first line */
    .search-icon,
    .search-tips-button,
    .clear-button {
        position: absolute;
        top: 0.875rem; /* Match textarea padding */
    }

    .search-icon {
        left: 1rem;
        color: #64748b;
        font-size: 0.875rem;
        pointer-events: none;
        z-index: 1;
    }

    .search-tips-button {
        right: 2.5rem;
        background: none;
        border: none;
        color: #64748b;
        padding: 0.25rem;
        cursor: pointer;
        transition: color 0.2s;
        z-index: 1;
    }

    .search-tips-button:hover {
        color: #2563eb;
    }

    .clear-button {
        position: absolute;
        right: 0.75rem;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        color: #64748b;
        padding: 0.25rem;
        cursor: pointer;
        transition: color 0.2s;
        z-index: 1;
    }

    .clear-button:hover {
        color: #ef4444;
    }

    /* Hide scrollbar but keep functionality */
    .search-input {
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
    }

    .search-input::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera */
    }

    .search-tips-dropdown {
        position: absolute;
        top: calc(100% + 0.5rem);
        left: 0;
        right: 0;
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        padding: 1rem;
        z-index: 50;
        border: 1px solid #e2e8f0;
        max-height: 400px;
        overflow-y: auto;
    }

    .search-tips-dropdown h4 {
        margin: 0 0 0.75rem 0;
        color: #1e293b;
        font-size: 0.875rem;
        font-weight: 600;
        border-bottom: 1px solid #e2e8f0;
        padding-bottom: 0.5rem;
    }

    .search-tips-content {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .search-tip-section h5 {
        margin: 0 0 0.5rem 0;
        color: #475569;
        font-size: 0.8125rem;
        font-weight: 600;
    }

    .search-tips-dropdown ul {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    .search-tips-dropdown li {
        margin-bottom: 0.5rem;
        color: #475569;
        font-size: 0.8125rem;
        line-height: 1.4;
        display: flex;
        align-items: flex-start;
        gap: 0.25rem;
    }

    .search-tips-dropdown code {
        background: #f1f5f9;
        padding: 0.125rem 0.25rem;
        border-radius: 4px;
        color: #2563eb;
        font-size: 0.75rem;
        font-family: monospace;
        white-space: nowrap;
    }

    .search-tips-dropdown .example {
        color: #0f766e;
        font-style: italic;
    }

    .complex-example {
        background: #f8fafc;
        padding: 0.75rem;
        border-radius: 6px;
        border-left: 3px solid #0f766e;
    }

    .example-description {
        margin: 0.5rem 0 0 0;
        font-size: 0.75rem;
        color: #475569;
        line-height: 1.4;
    }

    .close-tips {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        background: none;
        border: none;
        color: #64748b;
        padding: 0.25rem;
        cursor: pointer;
        transition: color 0.2s;
    }

    .close-tips:hover {
        color: #ef4444;
    }

    .counter {
        margin-bottom: 20px;
        font-size: 16px;
    }
    .evidence-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 16px;
        justify-content: center;
        animation: fadeIn 0.3s ease-in;
        margin-bottom: 40px; /* Increase space at bottom of list */
        width: 100%; /* Ensure full width is used */
    }
    .evidence-item {
        position: relative;
        display: flex;
        flex-direction: column;
        background: white;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        transition: transform 0.2s, box-shadow 0.2s;
        animation: fadeIn 0.3s ease-in-out forwards;
        opacity: 0;
        width: 100%;
        cursor: pointer;
        height: 100%;
    }

    /* Add styles for the admin menu */
    .admin-menu {
        position: absolute;
        top: 8px;
        left: 8px;
        z-index: 5;
    }

    .admin-menu-button {
        background: rgba(255, 255, 255, 0.7);
        border: none;
        border-radius: 4px;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: #94a3b8; /* Lighter color to be less prominent */
        font-size: 14px;
        padding: 0;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        transition: all 0.2s ease;
        opacity: 0.8; /* Start slightly transparent */
    }

    .admin-menu-button:hover {
        background: white;
        color: #2563eb; /* Blue on hover to indicate admin function */
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        opacity: 1;
    }

    .evidence-item:hover .admin-menu-button {
        opacity: 1; /* Full opacity on parent hover */
    }

    .admin-menu-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        margin-top: 4px;
        background: white;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 10;
        width: 180px;
        overflow: hidden;
    }

    /* Add hover state container for bookmark */
    .evidence-item:hover .bookmark-button {
        opacity: 1;
        transform: translateY(0);
    }

    /* Update bookmark button styles */
    .bookmark-button {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 10;
        background: rgba(0, 0, 0, 0.4);
        border: none;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
        opacity: 0;
        transform: translateY(-4px);
    }

    .evidence-item:hover .bookmark-button {
        opacity: 1;
        transform: translateY(0);
    }

    /* Update saved state styles */
    .bookmark-button.saved {
        opacity: 1;
        transform: translateY(0);
        background: rgba(37, 99, 235, 0.9);
    }

    .bookmark-button i {
        font-size: 1.1rem;
        color: white;
        transition: transform 0.2s ease;
    }

    .bookmark-button:hover {
        background: rgba(0, 0, 0, 0.75);
        transform: translateY(0);
    }

    .bookmark-button.saved:hover {
        background: rgba(37, 99, 235, 1);
    }

    .bookmark-button:active {
        transform: translateY(0) scale(0.95);
    }

    .bookmark-button:hover i {
        transform: scale(1.1);
    }

    /* Add save feedback animation */
    @keyframes savePopIn {
        0% { transform: scale(1); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
    }

    .bookmark-button.saved i {
        animation: savePopIn 0.3s ease;
    }

    /* Add tooltip */
    .bookmark-button::after {
        content: attr(title);
        position: absolute;
        bottom: -32px;
        right: 0;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        white-space: nowrap;
        opacity: 0;
        transform: translateY(-4px);
        transition: all 0.2s ease;
        pointer-events: none;
    }

    .bookmark-button:hover::after {
        opacity: 1;
        transform: translateY(0);
    }

    .image-container {
        width: 100%;
        height: 160px;
        position: relative;
        overflow: hidden;
    }

    .image-container img,
    .evidence-image {
        width: 100%;
        height: 133px;
        object-fit: cover;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.07);
        margin: 0;
        background: #f8fafc;
    }

    .evidence-item:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    }

    .evidence-item:hover .image-container img {
        transform: scale(1.05);
    }

    .content-wrapper {
        padding: 16px;
        min-height: 90px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        background-color: #ffffff;
        position: relative;
        overflow: visible;
    }
    .evidence-label {
        font-size: 14px;
        color: #1a1a1a;
        line-height: 1.5;
        font-weight: 500;
    }
    .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 200px;
    }
    .action-buttons {
        display: flex;
        gap: 8px;
        align-items: center;
    }
    .action-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 36px;
        font-size: 13px;
        font-weight: 500;
        border-radius: 8px;
        transition: all 0.2s ease;
        cursor: pointer;
        background: transparent;
        border: 1px solid #e2e8f0;
    }
    .save-button {
        width: 36px;
        color: #94a3b8;
    }
    .save-button:hover {
        border-color: #64748b;
        color: #64748b;
    }
    .save-button.saved {
        background: #f1f5f9;
        color: #0f172a;
        border-color: #e2e8f0;
    }
    .analyze-button {
        flex: 1;
        padding: 0 14px;
        gap: 6px;
        color: #64748b;
        background: #f8fafc;
    }
    .analyze-button i {
        font-size: 12px;
    }
    .analyze-button:hover {
        background: #f1f5f9;
        color: #0f172a;
    }
    .analyze-button:hover i {
        color: #007bff;
    }
    .analyze-button:active {
        transform: translateY(1px);
    }
    .action-button:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.1);
    }
    .action-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    .add-button {
        color: #007bff;
    }
    .evidence-detail {
        height: 100%;
        display: flex;
        flex-direction: column;
    }
    .breadcrumb {
        padding: 10px;
        border-bottom: 1px solid #ddd;
    }
    .back-button {
        background: none;
        border: none;
        color: #007bff;
        cursor: pointer;
        padding: 8px;
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .back-button:hover {
        color: #0056b3;
    }
    .detail-content {
        padding: 20px;
        flex: 1;
        overflow-y: auto;
    }
    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
    .empty-state {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 200px;
        color: #666;
        text-align: center;
        padding: 20px;
    }

    .empty-state i {
        color: #007bff;
        margin: 0 4px;
    }

    .evidence-menu {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 100;
    }

    .menu-button {
        background: none;
        border: none;
        font-size: 1.2em;
        cursor: pointer;
        padding: 4px 8px;
        color: #64748b;
        border-radius: 4px;
    }

    .menu-button:hover {
        background: rgba(0, 0, 0, 0.05);
        color: #333;
    }

    .menu-dropdown {
        position: absolute;
        right: 0;
        top: 100%;
        background: white;
        border: 1px solid #ddd;
        border-radius: 6px;
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        z-index: 101;
        min-width: 160px;
        margin-top: 4px;
    }

    .menu-item {
        width: 100%;
        padding: 8px 16px;
        border: none;
        background: none;
        text-align: left;
        cursor: pointer;
        white-space: nowrap;
        color: #333;
        font-size: 14px;
        transition: all 0.2s ease;
    }

    .menu-item:hover {
        background: #f5f5f5;
    }

    /* make evidence items look clickable */
    .evidence-item {
        cursor: pointer;
    }

    .evidence-item:hover {
        background: #f5f5f5;
    }

    /* Restore modal styles */
    .modal-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1050;
    }

    .modal-container {
        background: white;
        border-radius: 8px;
        width: 95%;
        height: 95%;
        max-width: 1400px;
        position: relative;
        overflow: hidden;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }

    /* Update content wrapper to accommodate absolute positioned menu */
    .content-wrapper {
        padding: 16px;
        min-height: 90px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        background-color: #ffffff;
        position: relative;
        overflow: visible;
    }

    .evidence-label {
        padding-right: 32px; /* Make space for menu button */
    }

    .search-button {
        position: absolute;
        right: 32px; /* Leave space for clear button */
        top: 50%;
        transform: translateY(-50%);
        background: #2563eb;
        color: white;
        border: none;
        border-radius: 6px;
        padding: 6px 12px;
        font-size: 13px;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .search-button:hover {
        background: #1d4ed8;
    }

    /* Update input styles to accommodate the search button */
    .input-container input {
        padding-right: 80px; /* Space for both buttons */
    }

    .spinner-container {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #64748b;
    }

    .loading-spinner {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;
    }

    .loading-spinner i {
        font-size: 2rem;
        color: #2563eb;
    }

    .spinner {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    .guide-section {
        padding: 1rem;
        background: #f8fafc;
        height: calc(100vh - 200px);
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
        will-change: transform;
    }

    .guide-content {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: flex-start;
    }

    .guide-iframe-container {
        position: relative;
        width: 100%;
        height: 100%;
        min-height: 800px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        overflow: hidden;
    }

    .guide-iframe {
        width: 100%;
        height: 100%;
        border: none;
        background: white;
    }

    .guide-loading {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
        color: #64748b;
        font-size: 0.875rem;
        background: rgba(255, 255, 255, 0.9);
        padding: 1rem;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .guide-loading .spinner {
        font-size: 1.5rem;
    }

    .guide-loading .bi-exclamation-triangle {
        font-size: 1.5rem;
        color: #ef4444;
    }

    @media (max-width: 768px) {
        .guide-section {
            padding: 0.5rem;
        }
    }

    .no-results {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem 1rem;
        text-align: center;
        color: #64748b;
        gap: 1rem;
    }

    .no-results i {
        font-size: 2rem;
        color: #94a3b8;
    }

    .no-results p {
        font-size: 0.9375rem;
        margin: 0;
    }

    .try-again-button {
        padding: 0.5rem 1rem;
        background: #2563eb;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .try-again-button:hover {
        background: #1d4ed8;
        transform: translateY(-1px);
    }

    .search-suggestion {
        margin: 1rem 0;
        padding: 1rem;
        background: #f0f9ff;
        border: 1px solid #bae6fd;
        border-radius: 6px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
    }

    .search-suggestion p {
        color: #0369a1;
        font-size: 0.875rem;
        margin: 0;
    }

    .delete-item {
        color: #ef4444;
    }

    .delete-item:hover {
        background: #fee2e2;
    }

    .delete-confirm {
        max-width: 400px;
        padding: 24px;
    }

    .delete-confirm-content {
        text-align: center;
    }

    .delete-confirm-content h3 {
        margin: 0 0 16px;
        color: #1e293b;
    }

    .delete-confirm-content p {
        margin: 0 0 24px;
        color: #64748b;
    }

    .delete-confirm-actions {
        display: flex;
        gap: 12px;
        justify-content: center;
    }

    .cancel-button {
        padding: 8px 16px;
        background: #f1f5f9;
        border: none;
        border-radius: 6px;
        color: #64748b;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .cancel-button:hover {
        background: #e2e8f0;
    }

    .delete-button {
        padding: 8px 16px;
        background: #ef4444;
        border: none;
        border-radius: 6px;
        color: white;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .delete-button:hover {
        background: #dc2626;
    }

    .update-modal {
        max-width: 500px;
        padding: 24px;
    }

    .update-form {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    .update-form h3 {
        margin: 0;
        color: #1e293b;
        font-size: 1.25rem;
    }

    .form-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .form-group label {
        font-weight: 500;
        color: #475569;
        font-size: 0.875rem;
    }

    .form-group input,
    .form-group textarea {
        padding: 8px 12px;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        font-size: 0.875rem;
        transition: all 0.2s ease;
    }

    .form-group input:focus,
    .form-group textarea:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
    }

    .form-actions {
        display: flex;
        gap: 12px;
        justify-content: flex-end;
        margin-top: 8px;
    }

    .save-button {
        padding: 8px 16px;
        background: #2563eb;
        border: none;
        border-radius: 6px;
        color: white;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .save-button:hover {
        background: #1d4ed8;
    }

    .evidence-description-wrapper {
        position: relative;
        margin-top: 8px;
    }

    .evidence-description {
        font-size: 13px;
        color: #64748b;
        line-height: 1.4;
        max-height: 3.6em; /* Show 3 lines by default */
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
        -webkit-box-orient: vertical;
        transition: max-height 0.3s ease;
        padding-right: 32px; /* More space for larger button */
    }

    .evidence-description.expanded {
        max-height: none;
    -webkit-line-clamp: unset;
    line-clamp: unset;
    }

    .expand-button {
        position: absolute;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.9);
        border: 1px solid #e2e8f0;
        border-radius: 4px;
        padding: 4px;
        cursor: pointer;
        color: #64748b;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }

    .expand-button:hover {
        color: #2563eb;
        background: white;
        border-color: #2563eb;
        transform: translateY(-1px);
    }

    .expand-button:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
    }

    .expand-button i {
        font-size: 14px;
    }

    .search-suggestion.warning {
        background: #fff7ed;
        border-color: #fed7aa;
    }

    .search-suggestion.warning p {
        color: #9a3412;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .search-suggestion.warning i {
        font-size: 1rem;
    }

    .simplified-terms {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 0.5rem;
    }

    .term-button {
        padding: 0.375rem 0.75rem;
        background: white;
        border: 1px solid #fdba74;
        border-radius: 4px;
        font-size: 0.8125rem;
        color: #ea580c;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .term-button:hover {
        background: #ffedd5;
        transform: translateY(-1px);
    }

    /* Update styles for search mode toggle */
    .search-mode-toggle {
        position: absolute;
        right: 3.75rem;
        top: 50%;
        transform: translateY(-50%);
        background: #f0f4f8;
        border: none;
        border-radius: 4px;
        padding: 0.25rem 0.75rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.75rem;
        color: #4b5563;
        cursor: pointer;
        transition: all 0.2s;
        z-index: 2;
    }

    .search-mode-toggle:hover {
        background: #e5e7eb;
        color: #1e40af;
    }

    .search-mode-toggle i {
        font-size: 1rem;
        color: #3b82f6;
    }

    .search-mode-toggle .search-mode-label {
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.02em;
    }

    /* When in advanced mode, use a different color */
    .search-mode-toggle.advanced {
        background: #eff6ff;
    }

    /* Update input padding to accommodate the indicator */
    .search-input {
        padding-right: 7rem;
    }

/* Custom styles for source filter dropdown */
    .filter-dropdown {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        /* margin-right: 10rem; */
        /* padding: 0.5rem 1rem; */
        height: 100%;
    }
.filter-label {
    font-size: 0.8125rem;
    color: #475569;
    font-weight: 500;
    margin-right: 0.5rem;
}
.filter-select {
    appearance: none;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.5rem 2.5rem 0.5rem 1rem;
    font-size: 0.8125rem;
    color: #475569;
    font-weight: 00;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
    transition: border-color 0.2s, box-shadow 0.2s;
    outline: none;
    cursor: pointer;
    min-width: 180px;
}
.filter-select:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37,99,235,0.12);
}
.filter-select option {
    color: #1e293b;
    background: #fff;
    font-size: 0.95rem;
}
/* Add dropdown arrow */
.filter-dropdown {
    position: relative;
}
.filter-select {
    position: relative;
}
.filter-dropdown::after {
    content: '\25BC';
    position: absolute;
    right: 1.5rem;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
    pointer-events: none;
    font-size: 0.8rem;
}

</style>


{#if selectedEvidence && showModal}
    <div 
        class="modal-backdrop" 
        transition:fade 
        on:click={closeModal}
    >
        <div 
            class="modal-container"
            on:click|stopPropagation
        >
            <pre style="display: none;"></pre>
            <EvidenceModal 
                evidence={selectedEvidence}
                isOpen={showModal}
                closeModal={closeModal}
                on:addToCanvas={(event) => dispatch('addImageToCanvas', event.detail)}
            />
        </div>
    </div>
{/if}

{#if deleteConfirmOpen}
    <div 
        class="modal-backdrop" 
        transition:fade 
        on:click={closeDeleteConfirm}
    >
        <div 
            class="modal-container delete-confirm"
            on:click|stopPropagation
        >
            <div class="delete-confirm-content">
                <h3>Delete Evidence</h3>
                <p>Are you sure you want to delete this evidence item? This action cannot be undone.</p>
                <div class="delete-confirm-actions">
                    <button 
                        class="cancel-button"
                        on:click={closeDeleteConfirm}
                    >
                        Cancel
                    </button>
                    <button 
                        class="delete-button"
                        on:click={() => deleteEvidence(evidenceToDelete)}
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    </div>
{/if}

{#if updateModalOpen}
    <div 
        class="modal-backdrop" 
        transition:fade 
        on:click={closeUpdateModal}
    >
        <div 
            class="modal-container update-modal"
            on:click|stopPropagation
        >
            <div class="update-form">
                <h3>Edit Evidence</h3>
                <div class="form-group">
                    <label for="label">Title</label>
                    <input 
                        type="text" 
                        id="label"
                        bind:value={updateForm.label}
                        placeholder="Enter title"
                    />
                </div>
                <div class="form-group">
                    <label for="description">Description</label>
                    <textarea 
                        id="description"
                        bind:value={updateForm.description}
                        placeholder="Enter description"
                        rows="4"
                    ></textarea>
                </div>
                <div class="form-actions">
                    <button 
                        class="cancel-button"
                        on:click={closeUpdateModal}
                    >
                        Cancel
                    </button>
                    <button 
                        class="save-button"
                        on:click={() => updateEvidence()}
                    >
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    </div>
{/if}

